import React, { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import api from '../services/api'
import ConfirmModal from './ConfirmModal'
import Icon from './Icon'
import { Button } from './ui'
import './EntityComments.css'

function formatDateTime(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function errorText(error, fallback) {
  return error?.response?.data?.error || error?.message || fallback
}

export default function EntityComments({ entityType, entityId }) {
  const { user } = useAuth()
  const [comments, setComments] = useState([])
  const [newContent, setNewContent] = useState('')
  const [editing, setEditing] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const canView = Boolean(user?.permissions?.['comments.view'])
  const canCreate = Boolean(user?.permissions?.['comments.create'])
  const canUpdate = Boolean(user?.permissions?.['comments.update'])
  const canDelete = Boolean(user?.permissions?.['comments.delete'])

  useEffect(() => {
    if (!canView || !entityType || !entityId) {
      setComments([])
      return undefined
    }
    let cancelled = false
    setLoading(true)
    setError('')
    api.get('/company/comments', { params: { entity_type: entityType, entity_id: entityId } })
      .then((response) => {
        if (!cancelled) setComments(Array.isArray(response.data?.comments) ? response.data.comments : [])
      })
      .catch((requestError) => {
        if (!cancelled) setError(errorText(requestError, 'Не удалось загрузить комментарии'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [canView, entityId, entityType])

  if (!canView || !entityType || !entityId) return null

  const addComment = async (event) => {
    event.preventDefault()
    const content = newContent.trim()
    if (!content || saving) return
    setSaving(true)
    setError('')
    try {
      const response = await api.post('/company/comments', {
        entity_type: entityType,
        entity_id: entityId,
        content,
      })
      const comment = response.data?.comment
      if (comment) setComments((current) => [comment, ...current])
      setNewContent('')
    } catch (requestError) {
      setError(errorText(requestError, 'Не удалось добавить комментарий'))
    } finally {
      setSaving(false)
    }
  }

  const saveEditedComment = async (commentId) => {
    const content = editing?.content?.trim()
    if (!content || saving) return
    setSaving(true)
    setError('')
    try {
      const response = await api.put(`/company/comments/${commentId}`, { content })
      const comment = response.data?.comment
      if (comment) {
        setComments((current) => current.map((item) => item.id === comment.id ? comment : item))
      }
      setEditing(null)
    } catch (requestError) {
      setError(errorText(requestError, 'Не удалось изменить комментарий'))
    } finally {
      setSaving(false)
    }
  }

  const deleteComment = async () => {
    if (!deleteTarget || saving) return
    setSaving(true)
    setError('')
    try {
      await api.delete(`/company/comments/${deleteTarget.id}`)
      setComments((current) => current.filter((item) => item.id !== deleteTarget.id))
      setDeleteTarget(null)
    } catch (requestError) {
      setError(errorText(requestError, 'Не удалось удалить комментарий'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="entity-comments-section entity-comments-section--flow card-section section-card" aria-label="Комментарии">
      <div className="entity-comments-header">
        <h2 className="ds-section-heading entity-comments-title">Комментарии</h2>
        <span className="entity-comments-count" aria-label={`Комментариев: ${comments.length}`}>{comments.length}</span>
      </div>

      {canCreate && (
        <form className="entity-comments-composer" onSubmit={addComment}>
          <label className="sr-only" htmlFor={`entity-comment-new-${entityType}-${entityId}`}>Текст комментария</label>
          <textarea
            id={`entity-comment-new-${entityType}-${entityId}`}
            className="ui-input entity-comments-textarea entity-comments-textarea--comfortable"
            value={newContent}
            onChange={(event) => setNewContent(event.target.value)}
            placeholder="Напишите, почему приняли это решение"
            rows={3}
          />
          <div className="entity-comments-composer-actions">
            <Button type="submit" variant="primary" disabled={saving || !newContent.trim()}>
              {saving ? 'Добавление…' : 'Добавить комментарий'}
            </Button>
          </div>
        </form>
      )}

      {error && <div className="message message-error entity-comments-message" role="alert">{error}</div>}
      {loading && <p className="entity-comments-state">Загрузка…</p>}

      {!loading && comments.length > 0 && (
        <div className="entity-comments-list entity-comments-list--padded">
          {comments.map((comment) => {
            const isEditing = editing?.id === comment.id
            return (
              <article className="entity-comment" key={comment.id}>
                <div className="entity-comment-meta">
                  <strong>{comment.user_display_name || '—'}</strong>
                  <div className="entity-comment-meta-tools">
                    <time dateTime={comment.created_at}>{formatDateTime(comment.created_at)}</time>
                    {!isEditing && (canUpdate || canDelete) && (
                      <div className="entity-comment-actions entity-comment-actions--inline">
                        {canUpdate && (
                          <Button type="button" variant="ghost" size="sm" className="entity-comment-icon-button" aria-label="Редактировать комментарий" title="Редактировать комментарий" onClick={() => setEditing({ id: comment.id, content: comment.content })}>
                            <Icon name="edit" size={16} alt="" aria-hidden="true" />
                          </Button>
                        )}
                        {canDelete && (
                          <Button type="button" variant="ghost" size="sm" className="entity-comment-icon-button entity-comment-delete" aria-label="Удалить комментарий" title="Удалить комментарий" onClick={() => setDeleteTarget(comment)}>
                            <Icon name="trash" size={16} alt="" aria-hidden="true" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {isEditing ? (
                  <div className="entity-comment-edit">
                    <label className="sr-only" htmlFor={`entity-comment-edit-${comment.id}`}>Редактирование комментария</label>
                    <textarea
                      id={`entity-comment-edit-${comment.id}`}
                      className="ui-input entity-comments-textarea entity-comments-textarea--comfortable"
                      value={editing.content}
                      onChange={(event) => setEditing({ id: comment.id, content: event.target.value })}
                      rows={3}
                    />
                    <div className="entity-comment-actions">
                      <Button type="button" variant="primary" size="sm" onClick={() => saveEditedComment(comment.id)} disabled={saving || !editing.content.trim()}>
                        Сохранить комментарий
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(null)} disabled={saving}>
                        Отмена
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="entity-comment-content">{comment.content}</p>
                )}
              </article>
            )
          })}
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Удалить комментарий?"
          message={<p>Комментарий будет скрыт из журнала. Это действие нельзя отменить.</p>}
          confirmLabel={saving ? 'Удаление…' : 'Удалить'}
          confirmDisabled={saving}
          onCancel={() => !saving && setDeleteTarget(null)}
          onConfirm={deleteComment}
          error={error || null}
        />
      )}
    </section>
  )
}
